import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, Trash2, ExternalLink } from "lucide-react";
import { toast } from "sonner";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";

const CATEGORIES = [
  { value: "commitment", label: "Commitment" },
  { value: "program", label: "Sponsored program" },
  { value: "metric", label: "Impact metric" },
];

type Commitment = {
  id: string;
  category: string;
  headline: string;
  body: string | null;
  metric_value: string | null;
  metric_label: string | null;
  sort_order: number;
  is_published: boolean;
};

export default function AnchorPageTab({ orgId, orgSlug, orgTier }: { orgId: string; orgSlug: string; orgTier: string }) {
  const qc = useQueryClient();

  const listQ = useQuery({
    queryKey: ["anchor-commitments", orgId],
    queryFn: async (): Promise<Commitment[]> => {
      const { data, error } = await supabase
        .from("anchor_commitments")
        .select("*")
        .eq("affiliate_id", orgId)
        .order("sort_order")
        .order("created_at");
      if (error) throw error;
      return (data ?? []) as Commitment[];
    },
  });

  const [draft, setDraft] = useState({
    category: "commitment",
    headline: "",
    body: "",
    metric_value: "",
    metric_label: "",
  });

  const create = useMutation({
    mutationFn: async () => {
      if (!draft.headline.trim()) throw new Error("Headline is required");
      const { error } = await supabase.from("anchor_commitments").insert({
        affiliate_id: orgId,
        category: draft.category,
        headline: draft.headline.trim(),
        body: draft.body.trim() || null,
        metric_value: draft.metric_value.trim() || null,
        metric_label: draft.metric_label.trim() || null,
        sort_order: (listQ.data?.length ?? 0),
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Added");
      setDraft({ category: "commitment", headline: "", body: "", metric_value: "", metric_label: "" });
      qc.invalidateQueries({ queryKey: ["anchor-commitments", orgId] });
    },
    onError: (e: any) => toast.error(e.message ?? "Could not add"),
  });

  const update = useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: Partial<Commitment> }) => {
      const { error } = await supabase.from("anchor_commitments").update(patch).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["anchor-commitments", orgId] }),
  });

  const del = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("anchor_commitments").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["anchor-commitments", orgId] }),
  });

  const isAnchor = orgTier === "anchor";

  return (
    <div className="space-y-6">
      <Card className="p-4 bg-muted/40 text-sm text-muted-foreground flex items-center justify-between gap-3 flex-wrap">
        <div>
          {isAnchor
            ? "Your co-branded landing page is live at "
            : "Anchor landing pages are unlocked for organizations at the Anchor tier. This editor previews the template — the public page shows only for Anchor tier orgs."}
          {isAnchor && (
            <a
              href={`/anchors/${orgSlug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-1 text-primary hover:underline inline-flex items-center gap-1"
            >
              /anchors/{orgSlug} <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
        {isAnchor ? (
          <Badge className="bg-primary/15 text-primary border-primary/30">Anchor tier</Badge>
        ) : (
          <Badge variant="outline">Preview only</Badge>
        )}
      </Card>

      <Card className="p-6 space-y-3">
        <h3 className="font-display text-lg">Add commitment / program / metric</h3>
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <Label>Type</Label>
            <Select value={draft.category} onValueChange={(v) => setDraft({ ...draft, category: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Headline</Label>
            <Input value={draft.headline} onChange={(e) => setDraft({ ...draft, headline: e.target.value })} maxLength={140} />
          </div>
          <div className="md:col-span-2">
            <Label>Body (optional)</Label>
            <Textarea rows={2} value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} />
          </div>
          {draft.category === "metric" && (
            <>
              <div>
                <Label>Metric value</Label>
                <Input value={draft.metric_value} onChange={(e) => setDraft({ ...draft, metric_value: e.target.value })} placeholder="$2.4M" />
              </div>
              <div>
                <Label>Metric label</Label>
                <Input value={draft.metric_label} onChange={(e) => setDraft({ ...draft, metric_label: e.target.value })} placeholder="Deployed to Mobile County" />
              </div>
            </>
          )}
        </div>
        <div>
          <Button onClick={() => create.mutate()} disabled={create.isPending}>
            {create.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
            Add
          </Button>
        </div>
      </Card>

      <div className="space-y-3">
        {listQ.isLoading ? (
          <div className="py-10 flex justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (listQ.data ?? []).length === 0 ? (
          <Card className="p-6 text-sm text-muted-foreground text-center">
            No commitments yet. Add 3–6 to populate your anchor landing page.
          </Card>
        ) : (
          (listQ.data ?? []).map((c) => (
            <Card key={c.id} className="p-4 flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
                  <Badge variant="outline">{c.category}</Badge>
                  {!c.is_published && <Badge variant="secondary">draft</Badge>}
                </div>
                <div className="font-display text-base mt-1">{c.headline}</div>
                {c.body && <p className="text-sm text-muted-foreground mt-1">{c.body}</p>}
                {(c.metric_value || c.metric_label) && (
                  <p className="text-sm mt-1">
                    <span className="font-display text-lg">{c.metric_value}</span>{" "}
                    <span className="text-muted-foreground">{c.metric_label}</span>
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => update.mutate({ id: c.id, patch: { is_published: !c.is_published } })}
                >
                  {c.is_published ? "Unpublish" : "Publish"}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => { if (confirm("Delete this item?")) del.mutate(c.id); }}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}